import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;
    const useMobileSuccessPopup = shouldAutoOpen && this.#shouldUseMobileSuccessPopup();

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (useMobileSuccessPopup) {
      event.promise
        ?.then(({ detail }) => {
          const settle = () => requestAnimationFrame(() => this.#updateStickyState());

          if (detail?.didError) {
            settle();
            return;
          }

          const showPopup = () => {
            this.#themeDrawer?.close?.();
            this.#showMobileSuccessPopup(event, detail);
            settle();
          };

          if (sourceModal?.open) {
            sourceModal.addEventListener('close', showPopup, { once: true });
            sourceModal.close();
          } else {
            showPopup();
          }
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
        });

      return;
    }

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #shouldUseMobileSuccessPopup() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   * @param {any} detail
   */
  #showMobileSuccessPopup(event, detail) {
    this.#injectMobileSuccessStyles();

    const existing = document.querySelector('.fanora-mobile-cart-success');
    existing?.remove();

    const addedLine = this.#findAddedCartLine(event, detail);
    const cartCount = Number(detail?.cart?.totalQuantity || detail?.items?.reduce?.((total, item) => total + Number(item.quantity || 0), 0) || 0);
    const title = this.#escapeHTML(addedLine?.product_title || addedLine?.title || 'Item added to your cart');
    const image = this.#normalizeImageUrl(addedLine?.image || addedLine?.featured_image?.url || addedLine?.featured_image);
    const options = this.#formatLineOptions(addedLine);
    const cartUrl = Theme.routes.cart_url || '/cart';

    const wrapper = document.createElement('div');
    wrapper.className = 'fanora-mobile-cart-success';
    wrapper.setAttribute('role', 'dialog');
    wrapper.setAttribute('aria-modal', 'true');
    wrapper.setAttribute('aria-label', 'Item added to cart');
    wrapper.innerHTML = `
      <div class="fanora-mobile-cart-success__backdrop" data-fanora-close></div>
      <div class="fanora-mobile-cart-success__panel">
        <button class="fanora-mobile-cart-success__close" type="button" aria-label="Close" data-fanora-close>&times;</button>
        <div class="fanora-mobile-cart-success__status">
          <span class="fanora-mobile-cart-success__check">✓</span>
          <span>Item added to your cart</span>
        </div>
        <div class="fanora-mobile-cart-success__product">
          ${image ? `<img class="fanora-mobile-cart-success__image" src="${this.#escapeHTML(image)}" alt="${title}">` : '<div class="fanora-mobile-cart-success__image fanora-mobile-cart-success__image--empty"></div>'}
          <div class="fanora-mobile-cart-success__product-info">
            <div class="fanora-mobile-cart-success__title">${title}</div>
            ${options ? `<div class="fanora-mobile-cart-success__options">${options}</div>` : ''}
          </div>
        </div>
        <a class="fanora-mobile-cart-success__button fanora-mobile-cart-success__button--secondary" href="${cartUrl}">View cart${cartCount ? ` (${cartCount})` : ''}</a>
        <a class="fanora-mobile-cart-success__button fanora-mobile-cart-success__button--primary" href="/checkout">Check out</a>
        <button class="fanora-mobile-cart-success__continue" type="button" data-fanora-close>Continue shopping</button>
      </div>
    `;

    wrapper.addEventListener('click', (clickEvent) => {
      if (clickEvent.target instanceof Element && clickEvent.target.hasAttribute('data-fanora-close')) {
        wrapper.remove();
        document.documentElement.classList.remove('fanora-mobile-cart-success-open');
      }
    });

    document.documentElement.classList.add('fanora-mobile-cart-success-open');
    document.body.appendChild(wrapper);
    wrapper.querySelector('.fanora-mobile-cart-success__close')?.focus();
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   * @param {any} detail
   */
  #findAddedCartLine(event, detail) {
    const lines = detail?.items || [];
    const addedVariantId = event.lines?.[0]?.merchandiseId?.toString?.();

    if (addedVariantId) {
      const matchedLine = lines.find((line) => line?.variant_id?.toString?.() === addedVariantId || line?.id?.toString?.() === addedVariantId);
      if (matchedLine) return matchedLine;
    }

    return lines[0] || null;
  }

  #formatLineOptions(line) {
    if (!line) return '';

    if (Array.isArray(line.options_with_values) && line.options_with_values.length) {
      return line.options_with_values
        .filter((option) => option?.value && option.value !== 'Default Title')
        .map((option) => `${this.#escapeHTML(option.name)}: ${this.#escapeHTML(option.value)}`)
        .join('<br>');
    }

    if (line.variant_title && line.variant_title !== 'Default Title') {
      return this.#escapeHTML(line.variant_title);
    }

    return '';
  }

  #normalizeImageUrl(imageUrl) {
    if (!imageUrl || typeof imageUrl !== 'string') return '';
    if (imageUrl.startsWith('//')) return `${window.location.protocol}${imageUrl}`;
    return imageUrl;
  }

  #escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #injectMobileSuccessStyles() {
    if (document.getElementById('fanora-mobile-cart-success-styles')) return;

    const style = document.createElement('style');
    style.id = 'fanora-mobile-cart-success-styles';
    style.textContent = `
      @media screen and (max-width: 749px) {
        html.fanora-mobile-cart-success-open,
        html.fanora-mobile-cart-success-open body {
          overflow: hidden;
        }

        .fanora-mobile-cart-success {
          position: fixed;
          inset: 0;
          z-index: 2147483000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          background: rgb(0 0 0 / 0.28);
        }

        .fanora-mobile-cart-success__backdrop {
          position: absolute;
          inset: 0;
        }

        .fanora-mobile-cart-success__panel {
          position: relative;
          z-index: 1;
          width: min(100%, 430px);
          max-height: calc(100vh - 48px);
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          background: var(--color-background, #fff);
          color: var(--color-foreground, #222);
          padding: 22px 22px 24px;
          box-shadow: 0 10px 36px rgb(0 0 0 / 0.16);
        }

        .fanora-mobile-cart-success__close {
          position: absolute;
          top: 14px;
          right: 16px;
          width: 32px;
          height: 32px;
          border: 0;
          background: transparent;
          color: currentColor;
          font-size: 30px;
          line-height: 1;
          cursor: pointer;
        }

        .fanora-mobile-cart-success__status {
          display: flex;
          align-items: center;
          gap: 12px;
          padding-right: 34px;
          color: var(--color-primary-button-background, currentColor);
          font-size: 18px;
          line-height: 1.3;
          margin-bottom: 24px;
        }

        .fanora-mobile-cart-success__check {
          font-size: 22px;
          line-height: 1;
        }

        .fanora-mobile-cart-success__product {
          display: grid;
          grid-template-columns: 82px 1fr;
          gap: 18px;
          align-items: center;
          margin-bottom: 26px;
        }

        .fanora-mobile-cart-success__image {
          width: 82px;
          height: 82px;
          object-fit: cover;
          background: rgb(var(--color-foreground-rgb, 0 0 0) / 0.04);
        }

        .fanora-mobile-cart-success__image--empty {
          display: block;
        }

        .fanora-mobile-cart-success__title {
          font-size: 20px;
          line-height: 1.25;
          color: inherit;
          margin-bottom: 8px;
        }

        .fanora-mobile-cart-success__options {
          font-size: 16px;
          line-height: 1.35;
          color: rgb(var(--color-foreground-rgb, 0 0 0) / 0.72);
        }

        .fanora-mobile-cart-success__button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          min-height: 54px;
          text-decoration: none;
          text-align: center;
          font-size: 18px;
          line-height: 1.2;
          letter-spacing: 0.06em;
          margin-top: 14px;
          border: var(--style-border-width-primary, 1px) solid var(--color-primary-button-border, currentColor);
        }

        .fanora-mobile-cart-success__button--secondary {
          color: var(--color-primary-button-background, currentColor);
          background: transparent;
        }

        .fanora-mobile-cart-success__button--primary {
          color: var(--color-primary-button-text, #fff);
          background: var(--color-primary-button-background, #111);
          border-color: var(--color-primary-button-background, #111);
        }

        .fanora-mobile-cart-success__continue {
          display: block;
          margin: 16px auto 0;
          border: 0;
          background: transparent;
          color: var(--color-primary-button-background, currentColor);
          text-decoration: underline;
          text-underline-offset: 0.18em;
          font: inherit;
          font-size: 18px;
          cursor: pointer;
        }
      }
    `;

    document.head.appendChild(style);
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}